const PREFETCH_REGIONS = ['products', 'collections', 'pages', 'blogs', 'policies', 'search'];
const PREFETCH_LINK_REGEX = new RegExp(`^(\\/|(\\/(?:${PREFETCH_REGIONS.join('|')})(\\/.*)?))$`);
const SUPPORTS_SPECULATION = HTMLScriptElement.supports?.('speculationrules') ?? false;
const linkCache = new Set();
const listenerMap = new WeakMap();

/**
 * @typedef {'mouseover' | 'intersection'} PrefetchMethod
 */

/**
 * Returns the preferred method for prefetching based on the window size.
 * @returns {PrefetchMethod} The preferred method for prefetching. Possible values are 'intersection' or 'mouseover'.
 */
const getPrefetchMethod = () =>
  window.matchMedia('(max-width: 768px)').matches ? 'intersection' : 'mouseover';

/**
 * Determine if devices should even consider prefetching
 * Low power mode, data saver, etc.
 * @returns {boolean} Whether or not prefetching should be used.
 */
const shouldUsePrefetching = async () => {
  if (navigator.connection?.saveData) return false;
  if (navigator.getBattery) {
    const battery = await navigator.getBattery();
    if (battery.level < 0.2) return false;
  }
  return true;
}

/**
 * Prefetches a page by adding a <link> element to the document's head.
 * @param {string} href - The URL of the page to prefetch.
 * @param {string} [priority='auto'] - The priority of the prefetch request. Defaults to 'auto'.
 */
function addPrefetchLink(href, priority = 'auto') {
  const origin = window.location.origin;
  if (!href?.startsWith('/') && !href?.startsWith(origin)) return;
  if (window.location.href === href || linkCache.has(href)) return;

  linkCache.add(href);

  try {
    if (SUPPORTS_SPECULATION) {
      const specRuleScript = document.createElement('script');
      specRuleScript.type = 'speculationrules';
      specRuleScript.textContent = JSON.stringify({
        prefetch: [{ urls: [href], eagerness: 'immediate' }]
      });
      document.body.append(specRuleScript);
    } else {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = href;
      link.fetchPriority = priority;
      link.as = 'document';
      document.head.appendChild(link);
    }
  } catch (e) {
    console.error('Failed to prefetch page:', href, e);
  }
}

/**
 * Prefetches pages based on the specified method.
 * @param {PrefetchMethod} deviceMethod - The method to use for prefetching pages.
 */
function initPagePrefetching(deviceMethod) {
  if (!deviceMethod || !['mouseover', 'intersection'].includes(deviceMethod)) return;

  shouldUsePrefetching().then((should) => {
    if (!should) return;

    const observer = deviceMethod === 'intersection' ?
      new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            addPrefetchLink(entry.target.href, 'high');
            obs.unobserve(entry.target);
          }
        });
      }) : null;

    const cleanup = () => {
      document.querySelectorAll('a[href]').forEach(link => {
        if (deviceMethod === 'mouseover') {
          const handler = listenerMap.get(link);
          if (handler) link.removeEventListener('mouseover', handler);
        } else if (observer) {
          observer.unobserve(link);
        }
      });
      observer?.disconnect();
      linkCache.clear();
    };

    cleanup();

    document.querySelectorAll('a[href]').forEach((link) => {
      if (!PREFETCH_LINK_REGEX.test(link.pathname)) return;

      if (deviceMethod === 'mouseover') {
        const handler = () => addPrefetchLink(link.href, 'high');
        listenerMap.set(link, handler);
        link.addEventListener('mouseover', handler);
      } else {
        observer.observe(link);
      }
    });
  });
}

document.addEventListener('DOMContentLoaded', () => initPagePrefetching(getPrefetchMethod()));
window.addEventListener('resize', debounce(() => initPagePrefetching(getPrefetchMethod()), 200));
